import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useGeri, useSekme } from '@/lib/geri';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Edit, Package, Warehouse, ArrowLeftRight, TrendingUp, TrendingDown, Minus, FileText, BookOpen, CornerUpRight } from 'lucide-react';
// Yeni sayı gösterimi `lib/sayi.js`den: `toFixed` ondalığı NOKTA yazar ve
// Türkçede bu BİNLİK ayraç sanılıyor (bkz. ERP.md).
import { trSayi, trPara } from '@/lib/sayi';
import { toast } from 'sonner';

const typeLabels = { 
  raw_material: 'Hammadde', semi_finished: 'Yarı Mamul', finished: 'Mamul', 
  consumable: 'Sarf Malzeme', fixed_asset: 'Demirbaş', guest_consumption: 'Misafir Tüketim',
  trade_goods: 'Ticari Mal', other: 'Diğer'
};
const typeColors = {
  raw_material: 'bg-orange-100 text-orange-800',
  semi_finished: 'bg-green-100 text-green-800',
  finished: 'bg-blue-100 text-blue-800',
  consumable: 'bg-purple-100 text-purple-800',
  fixed_asset: 'bg-gray-200 text-gray-700',
  guest_consumption: 'bg-red-100 text-red-800',
  trade_goods: 'bg-cyan-100 text-cyan-800',
  other: 'bg-gray-100 text-gray-600'
};
const uretimTipiLabels = { taze: 'Taze', on_hazirlikli: 'Ön Hazırlıklı' };
const movementLabels = {
  purchase_in: 'Satin Alma', production_in: 'Uretim Girisi', production_out: 'Uretim Cikisi',
  transfer_in: 'Transfer Girisi', transfer_out: 'Transfer Cikisi', sale_out: 'Satis',
  waste: 'Fire/Zayi', count_adjustment: 'Sayim Farki', return: 'Iade',
  consumption_out: 'Tuketim'
};

export default function ProductDetail() {
  // Stok miktarı `stock_quantity` iznine bağlı (sunucu da göndermez)
  const { hasPermission } = useAuth();
  const stokGorebilir = hasPermission('stock_quantity', 'view');
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/products');
  const [product, setProduct] = useState(null);
  const [kategoriler, setKategoriler] = useState({});   // id -> kategori (ana / alt / masraf grubu yolu için)
  usePageTitle(product?.name || 'Ürün Detayı');
  const [stock, setStock] = useState([]);
  const [movements, setMovements] = useState([]);
  // Reçete bağlantıları AYRI istekte: `recipes` yetkisi olmayan kullanıcıda
  // 403 dönüyor ve ana Promise.all'a girse bütün sayfayı çökertirdi.
  const receteGorebilir = hasPermission('recipes', 'view');
  // Açık sekme adreste: faturaya gidip GERİ dönen kullanıcı aynı sekmeyi görür.
  const [sekmeAdres, sekmeSec] = useSekme('stock');
  // Yetkisi olmayan sekme adresten gelirse (paylaşılan bağlantı) boş ekran yerine Stok.
  const sekme = sekmeAdres === 'recipes' && !receteGorebilir ? 'stock' : sekmeAdres;
  const [receteler, setReceteler] = useState(null);

  useEffect(() => {
    // Kategori yolu ayrı istek: ana Promise.all'ın sırasını bozmasın
    api.get('/categories').then(r => setKategoriler(Object.fromEntries((r.data || []).map(c => [c.id, c])))).catch(() => {});
    Promise.all([
      api.get(`/products/${id}`),
      api.get(`/products/${id}/stock`),
      api.get('/stock/movements', { params: { product_id: id, per_page: 100 } })
    ]).then(([pRes, sRes, mRes]) => {
      setProduct(pRes.data);
      setStock(sRes.data);
      setMovements(mRes.data.items || mRes.data);
    }).catch(() => toast.error('Urun yuklenemedi'));
  }, [id]);

  useEffect(() => {
    if (!receteGorebilir) return;
    api.get(`/recipes/urun/${id}`)
      .then(r => setReceteler(r.data))
      .catch(() => setReceteler({ urunun_receteleri: [], kullanildigi_receteler: [] }));
  }, [id, receteGorebilir]);

  // Hareketler iş tarihine göre yeniden eskiye sıralanır ve her hareket için bir
  // ÖNCEKİ alış birim fiyatı bulunur (fiyatsız hareketler — devir/fire — atlanır),
  // böylece fiyat artış/azalışı karşılaştırılabilir.
  const movementsWithTrend = useMemo(() => {
    const eff = (m) => String(m.movement_date || m.created_at || '');
    const yeniDenEskiye = [...movements].sort((a, b) => eff(b).localeCompare(eff(a)));
    const oncekiFiyat = {};
    let prev = null;
    for (const m of [...yeniDenEskiye].reverse()) {      // eskiden yeniye tara
      const p = parseFloat(m.unit_cost);
      if (p > 0) {
        oncekiFiyat[m.id] = prev;
        prev = p;
      }
    }
    return yeniDenEskiye.map(m => ({ ...m, _oncekiFiyat: oncekiFiyat[m.id] ?? null }));
  }, [movements]);

  if (!product) return <div className="text-center py-8 text-[#8a7e70]">Yukleniyor...</div>;

  const totalStock = stock.reduce((sum, s) => sum + (s.quantity || 0), 0);

  return (
    <div data-testid="product-detail-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">{product.name}</h1>
            <p className="text-sm text-[#8a7e70] font-mono">{product.code}</p>
            {[product.category_id, product.subcategory_id, product.sinif_id].some(Boolean) && (
              <p className="text-sm text-[#5c554b] mt-0.5" data-testid="product-kategori-yolu">
                {[['Kategori', product.category_id], ['Alt kategori', product.subcategory_id], ['Masraf grubu', product.sinif_id]]
                  .filter(([, kid]) => kid && kategoriler[kid])
                  .map(([etiket, kid]) => {
                    const c = kategoriler[kid];
                    return <span key={kid} title={etiket}>{c.kod ? `${c.kod} · ` : ''}{c.name}</span>;
                  })
                  .reduce((acc, el, i) => (i ? [...acc, <span key={`ok-${i}`} className="mx-1 text-[#a3968a]">›</span>, el] : [el]), [])}
              </p>
            )}
            {product.description && (
              <p className="text-sm text-[#3F3E39] mt-1 max-w-3xl whitespace-pre-line" data-testid="product-description">
                {product.description}
              </p>
            )}
          </div>
        </div>
        <Link to={`/erp/products/${id}/edit`}>
          <Button className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="edit-product-btn">
            <Edit size={16} className="mr-2" /> Duzenle
          </Button>
        </Link>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <InfoCard label="Tur" value={
          <span className={`inline-block px-2 py-0.5 rounded text-sm ${typeColors[product.type] || 'bg-gray-100 text-gray-600'}`}>
            {typeLabels[product.type] || product.type}
          </span>
        } />
        <InfoCard label="Birim" value={product.unit} />
        <InfoCard label="Toplam Stok" value={stokGorebilir ? `${totalStock.toFixed(1)} ${product.unit}` : 'yetkiniz yok'} />
        <InfoCard label="Son Fiyat" value={product.last_purchase_price ? `${product.last_purchase_price.toFixed(2)} TL` : '-'} />
      </div>

      <Tabs value={sekme} onValueChange={sekmeSec} className="w-full">
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock">
            <Warehouse size={14} className="mr-1.5" /> Stok Durumu
          </TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">
            <ArrowLeftRight size={14} className="mr-1.5" /> Hareketler
          </TabsTrigger>
          {receteGorebilir && (
            <TabsTrigger value="recipes" data-testid="tab-recipes">
              <BookOpen size={14} className="mr-1.5" /> Reçeteler
              {receteler && (
                <span className="ml-1.5 text-[10px] text-[#8a7e70] tabular-nums">
                  ({receteler.urunun_receteleri.length + receteler.kullanildigi_receteler.length})
                </span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="stock">
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Depo</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Miktar</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stock.map((s) => (
                    <TableRow key={s.id} className="border-[#d0c4b0]">
                      <TableCell className="font-medium text-[#1a1916]">{s.warehouse_name}</TableCell>
                      <TableCell className="text-right font-semibold text-[#1a1916]">
                        {stokGorebilir ? s.quantity?.toFixed(2) : <span className="text-[#b5aa9a] italic font-normal text-xs">gizli</span>}
                      </TableCell>
                      <TableCell className="text-[#8a7e70]">{s.unit}</TableCell>
                    </TableRow>
                  ))}
                  {stock.length === 0 && (
                    <TableRow><TableCell colSpan={3} className="text-center py-6 text-[#8a7e70]">Stok kaydı yok</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movements">
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tur</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Belge</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Firma</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Depo</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Miktar</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Birim Fiyat</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movementsWithTrend.map((m) => (
                    <TableRow key={m.id} className="border-[#d0c4b0]">
                      {/* İş tarihi (faturanın tarihi) gösterilir; kaydın oluşturulma
                          tarihi değil — aksi halde geçmiş tarihli faturalar toplu
                          girildiği gün girmiş gibi görünüyordu. */}
                      <TableCell className="text-sm text-[#3F3E39]">
                        {new Date(m.movement_date || m.created_at).toLocaleDateString('tr-TR')}
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${m.quantity >= 0 ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' : 'bg-[#7a3535]/10 text-[#7a3535]'}`}>
                          {movementLabels[m.movement_type] || m.movement_type}
                        </span>
                      </TableCell>
                      {/* Hareketin doğduğu belge. Satın alma satırından faturaya
                          buradan gidilir; devir (açılış) hareketlerinin belgesi yoktur. */}
                      <TableCell className="text-sm">
                        {m.source_document?.tur === 'invoice' ? (
                          <Link to={`/erp/invoices/${m.source_document.id}`}
                            title={`Faturaya git${m.source_document.tarih ? ` (${m.source_document.tarih})` : ''}`}
                            className="inline-flex items-center gap-1.5 text-[#3d6e4e] hover:underline font-medium"
                            data-testid={`movement-invoice-${m.id}`}>
                            <FileText size={13} className="shrink-0" />
                            {m.source_document.no || 'Fatura'}
                          </Link>
                        ) : m.source_document?.tur === 'purchase_receipt' ? (
                          <span className="text-[#8a7e70]" title="Mal kabul kaydı">
                            {m.source_document.no || 'Mal kabul'}
                          </span>
                        ) : (
                          <span className="text-[#b5aa9a]">—</span>
                        )}
                      </TableCell>
                      {/* Devir (açılış) hareketlerinin firması yoktur */}
                      <TableCell className="text-sm text-[#3F3E39]">{m.supplier_name || '—'}</TableCell>
                      <TableCell className="text-sm text-[#3F3E39]">{m.warehouse_name}</TableCell>
                      <TableCell className={`text-right font-semibold ${m.quantity >= 0 ? 'text-[#3d6e4e]' : 'text-[#7a3535]'}`}>
                        {m.quantity > 0 ? '+' : ''}{m.quantity?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-sm text-[#8a7e70]">{m.unit || product.unit}</TableCell>
                      {/* Birim fiyat + bir önceki alış fiyatına göre değişim.
                          Alışta fiyat artışı olumsuzdur → kırmızı; düşüş → yeşil. */}
                      <TableCell className="text-right">
                        {(() => {
                          const fiyat = parseFloat(m.unit_cost) || 0;
                          if (!fiyat) return <span className="text-[#8a7e70]">-</span>;
                          const onceki = m._oncekiFiyat;
                          const yuzde = onceki > 0 ? ((fiyat - onceki) / onceki) * 100 : null;
                          const artis = yuzde != null && yuzde > 0.05;
                          const azalis = yuzde != null && yuzde < -0.05;
                          const Ikon = artis ? TrendingUp : azalis ? TrendingDown : Minus;
                          const renk = artis ? 'text-[#7a3535]' : azalis ? 'text-[#3d6e4e]' : 'text-[#b5aa9a]';
                          return (
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="font-mono text-sm text-[#1a1916] tabular-nums">
                                {fiyat.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                              {yuzde != null ? (
                                <span className={`flex items-center gap-0.5 ${renk}`}
                                  title={`Önceki alış: ${onceki.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`}>
                                  <Ikon size={13} />
                                  <span className="text-[10px] font-semibold tabular-nums w-11 text-left">
                                    {(yuzde > 0 ? '+' : '') + yuzde.toFixed(1)}%
                                  </span>
                                </span>
                              ) : (
                                <span className="text-[10px] text-[#b5aa9a] w-[62px] text-left" title="Karşılaştırılacak önceki alış yok">ilk alış</span>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-[#3F3E39]">
                        {m.total_cost ? `${Math.abs(m.total_cost).toFixed(2)} TL` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {movements.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center py-6 text-[#8a7e70]">Hareket bulunamadi</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── REÇETELER: iki yön ayrı tabloda ──
            Üstte "bu ürünü ÜRETEN reçeteler", altta "bu ürünü MALZEME olarak
            kullanan reçeteler". İki yönü tek tabloda birleştirmek, aynı
            satırın hangi yöne ait olduğunu belirsiz bırakıyordu. */}
        {receteGorebilir && (
          <TabsContent value="recipes">
            {!receteler ? (
              <p className="text-center py-8 text-[#8a7e70]">Yükleniyor...</p>
            ) : (
              <div className="space-y-4">
                <Card className="bg-[#faf7f2] border-[#d0c4b0]">
                  <CardHeader className="border-b border-[#d0c4b0] py-3">
                    <CardTitle className="text-base font-bold text-[#1a1916] flex items-center gap-2">
                      <BookOpen size={15} className="text-[#3d6e4e]" />
                      Ürünün Reçeteleri
                      <span className="text-xs font-normal text-[#8a7e70]">
                        bu ürünü üreten reçeteler ({receteler.urunun_receteleri.length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Versiyon</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Geçerlilik</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Üretim Tipi</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Çıktı</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Malzeme</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Toplam Maliyet</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Birim Maliyet</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {receteler.urunun_receteleri.map((r) => (
                          <TableRow key={r.recipe_id} className="border-[#d0c4b0]">
                            <TableCell>
                              <Link to={`/erp/recipes/${r.recipe_id}`}
                                className="text-[#3d6e4e] hover:underline font-semibold"
                                data-testid={`recete-${r.recipe_id}`}>
                                v{r.version}
                              </Link>
                              {r.is_active && (
                                <Badge className="ml-2 bg-[#3d6e4e]/10 text-[#3d6e4e] text-[10px]">Geçerli</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-[#3F3E39]">
                              {r.valid_from ? new Date(r.valid_from).toLocaleDateString('tr-TR') : '—'}
                            </TableCell>
                            <TableCell className="text-sm text-[#3F3E39]">
                              {uretimTipiLabels[r.production_type] || r.production_type || '—'}
                            </TableCell>
                            <TableCell className="text-right text-sm text-[#1a1916]">
                              {trSayi(r.yield_quantity)} <span className="text-[#8a7e70]">{r.yield_unit || ''}</span>
                            </TableCell>
                            <TableCell className="text-right text-sm text-[#3F3E39] tabular-nums">{r.malzeme_sayisi}</TableCell>
                            <TableCell className="text-right font-mono text-sm text-[#1a1916]">
                              {r.toplam_maliyet != null ? `${trPara(r.toplam_maliyet)} TL` : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-[#1a1916]">
                              {r.birim_maliyet != null
                                ? `${trPara(r.birim_maliyet)} TL/${r.yield_unit || product.unit}`
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {receteler.urunun_receteleri.length === 0 && (
                          <TableRow><TableCell colSpan={7} className="text-center py-6 text-[#8a7e70]">
                            Bu ürünün reçetesi yok
                          </TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card className="bg-[#faf7f2] border-[#d0c4b0]">
                  <CardHeader className="border-b border-[#d0c4b0] py-3">
                    <CardTitle className="text-base font-bold text-[#1a1916] flex items-center gap-2">
                      <CornerUpRight size={15} className="text-[#7a6a2e]" />
                      Kullanıldığı Reçeteler
                      <span className="text-xs font-normal text-[#8a7e70]">
                        bu ürünü malzeme olarak kullanan reçeteler ({receteler.kullanildigi_receteler.length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Reçete Ürünü</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tür</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Versiyon</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Kullanılan Miktar</TableHead>
                          <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Reçete Çıktısı</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {receteler.kullanildigi_receteler.map((r) => (
                          <TableRow key={r.recipe_id} className="border-[#d0c4b0]">
                            <TableCell>
                              <Link to={`/erp/recipes/${r.recipe_id}`}
                                className="text-[#3d6e4e] hover:underline font-medium"
                                data-testid={`kullanan-${r.recipe_id}`}>
                                {r.product_name || '—'}
                              </Link>
                              <span className="block text-[11px] font-mono text-[#8a7e70]">{r.product_code || ''}</span>
                            </TableCell>
                            <TableCell>
                              <span className={`inline-block px-2 py-0.5 rounded text-xs ${typeColors[r.product_type] || 'bg-gray-100 text-gray-600'}`}>
                                {typeLabels[r.product_type] || r.product_type || '—'}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-[#3F3E39]">
                              v{r.version}
                              {r.is_active
                                ? <Badge className="ml-2 bg-[#3d6e4e]/10 text-[#3d6e4e] text-[10px]">Geçerli</Badge>
                                : <span className="ml-2 text-[10px] text-[#b5aa9a]">eski versiyon</span>}
                            </TableCell>
                            {/* Aynı malzeme birden çok satırda geçebiliyor; sunucu
                                birim başına topluyor ve kaç satır olduğunu söylüyor. */}
                            <TableCell className="text-right text-sm text-[#1a1916]">
                              {(r.miktarlar || []).map((m, i) => (
                                <span key={i} className="block tabular-nums">
                                  {trSayi(m.miktar)} <span className="text-[#8a7e70]">{m.birim}</span>
                                </span>
                              ))}
                              {r.satir_sayisi > 1 && (
                                <span className="text-[10px] text-[#8a7e70]">{r.satir_sayisi} satırda</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-sm text-[#3F3E39]">
                              {trSayi(r.yield_quantity)} <span className="text-[#8a7e70]">{r.yield_unit || ''}</span>
                            </TableCell>
                          </TableRow>
                        ))}
                        {receteler.kullanildigi_receteler.length === 0 && (
                          <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#8a7e70]">
                            Bu ürün hiçbir reçetede malzeme olarak kullanılmıyor
                          </TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardContent className="p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#8a7e70] mb-1">{label}</p>
        <p className="text-lg font-semibold text-[#1a1916]">{value}</p>
      </CardContent>
    </Card>
  );
}
